import { isNumber, unique } from 'radash';

import { ConnectionHelpers } from '@diagram/helpers/ConnectionHelpers';
import { LabelService } from '@diagram/services/LabelService';
import { FaultSimulationCacheService } from '@diagram/state/fault-simulation-cache';

import {
    ComponentType,
    currentConverter,
    currentSlewRateConverter,
    Diagram,
    DiagramComponentInstance,
    DiagramConnection,
    FaultSimulation,
    Fuse,
    getFaultCurrentSeries,
    getFaultTouchVoltage,
    i2tConverter,
    isProtectionDevice,
    JobStatus,
    Simulation,
    TCurrentUnit,
    TVoltageUnit,
    voltageConverter,
    Zone,
} from 'models';
import { FormatHelpers } from 'helpers/formatters';
import { NumberHelpers } from 'helpers/NumberHelpers';

import { DateService } from 'services/DateService';

import { getMinAndMaxVoltageForZone } from '../validators/zones/zone-voltage-levels';
import { convertIncidentEnergy, JPM2_PER_CALPCM2, pickEArcDisplayUnit } from './ArcFlashHelpers';
import { ZoneHelpers } from './ZoneHelpers';

export type CriticalClearingTimeResult = {
    zone: Zone;
    minVoltage: number;
    source: 'custom' | 'zone' | 'inherited';
    simulationId?: string;
    componentInstances?: string[];
    criticalClearingTime: number | null;
    firstCrossingComponents?: { componentInstanceId: string; port: string }[];
};

export type FuseI2TResult = {
    fuseId: string;
    fuseLabel: string;
    i2t: number;
    meltingI2t?: number;
    meltingTime?: number;
    isFaultFuse?: boolean;
    totalClearingI2t?: number;
    totalClearingTime?: number;
};

export type ProtectionMetric = 'current' | 'i2t' | 'voltage';

export type ProtectionDeviceStatus =
    | 'tripped'
    | 'tripped-time-unknown'
    | 'did-not-trip'
    | 'inconclusive'
    | 'not-modelled'
    | 'not-reported';

export type ProtectionDeviceState = {
    status: ProtectionDeviceStatus;
    open: boolean;
    tripTime: number | null;
    metric: ProtectionMetric;
    value?: number;
    valueUnit?: string;
    threshold?: number;
    thresholdUnit?: string;
    reason?: string;
    unitMismatch?: boolean;
};

export const EXPECTED_PROTECTION_UNITS: Record<ProtectionMetric, string> = {
    current: 'A',
    i2t: 'A^2 s',
    voltage: 'V',
};

const PROTECTION_METRICS: Partial<Record<DiagramComponentInstance['componentType'], ProtectionMetric>> = {
    fuse: 'i2t',
    converter: 'voltage',
};

const hasUnexpectedUnit = (metric: ProtectionMetric, unit?: string) =>
    !!unit && unit !== EXPECTED_PROTECTION_UNITS[metric];

export type PeakCurrentResult = {
    componentId: string;
    componentLabel: string;
    componentType: ComponentType;
    peakCurrent: number;
    isFaultDevice?: boolean;
};

type FaultLine = 'L+' | 'M' | 'L-' | 'PE';

const FAULT_TERMINAL_PAIR_LABELS: { lines: FaultLine[]; label: string }[] = [
    { lines: ['L+', 'L-'], label: 'L+ ↔ L-' },
    { lines: ['L+', 'M'], label: 'L+ ↔ M' },
    { lines: ['L-', 'M'], label: 'L- ↔ M' },
    { lines: ['L+', 'PE'], label: 'L+ ↔ PE' },
    { lines: ['M', 'PE'], label: 'M ↔ PE' },
    { lines: ['L-', 'PE'], label: 'L- ↔ PE' },
];

const subtractVoltageRails = (a: number[], b: number[]): number[] => {
    const length = Math.max(a.length, b.length);
    return Array.from({ length }, (_, index) => {
        const aValue = a[index] ?? a[a.length - 1];
        const bValue = b[index] ?? b[b.length - 1];
        return aValue - bValue;
    });
};

// One hue per component, assigned in the order the result lists them. Adjacent entries stay
// apart under deuteranopia and protanopia, so keep the order when adding to it.
const COMPONENT_INSTANCE_COLORS = [
    'blue.7',
    'orange.8',
    'teal.7',
    'grape.7',
    'yellow.9',
    'violet.7',
    'pink.7',
    'cyan.7',
];

export type ConverterLimitKind = 'undervoltage' | 'overvoltage' | 'overcurrent';

/**
 * A converter port that left one of its declared limits during a fault, and whether the
 * timestamp the viewer is on is at or past that crossing.
 */
export type ConverterLimitState = {
    kind: ConverterLimitKind;
    portIndex: number;
    threshold: number;
    crossingIndex: number;
    crossingTime: number;
    tripped: boolean;
};

type PortLines = Partial<Record<FaultLine, { voltage?: number[]; current?: number[] }>>;

const DC_TERMINAL_PAIRS: [FaultLine, FaultLine][] = [
    ['L+', 'L-'],
    ['L+', 'M'],
    ['M', 'L-'],
];

const DC_CURRENT_TERMINALS: FaultLine[] = ['L+', 'L-', 'M'];

const getDcTerminalVoltage = (port: PortLines | undefined): number[] | null => {
    for (const [positive, reference] of DC_TERMINAL_PAIRS) {
        const positiveSeries = port?.[positive]?.voltage;
        const referenceSeries = port?.[reference]?.voltage;

        if (!Array.isArray(positiveSeries) || !Array.isArray(referenceSeries)) continue;
        if (positiveSeries.length === 0 || referenceSeries.length === 0) continue;

        return subtractVoltageRails(positiveSeries, referenceSeries);
    }

    return null;
};

/**
 * The magnitude of the pole current on one DC port. The engine signs a terminal current by
 * the direction it was drawn in, and a limit is symmetric, so the sign carries nothing the
 * comparison needs. The first terminal that carries a series wins: on a three-wire port L+
 * and L- are the same pole current with opposite signs.
 */
const getDcPortCurrent = (port: PortLines | undefined): number[] | null => {
    for (const terminal of DC_CURRENT_TERMINALS) {
        const series = port?.[terminal]?.current;

        if (!Array.isArray(series) || series.length === 0) continue;

        return series.map((value) => (isNumber(value) ? Math.abs(value) : value));
    }

    return null;
};

/**
 * One declared limit a converter port can leave: where the threshold is read from, which
 * series it is compared against, which side of it counts as outside, and how long the
 * excursion has to last before it is an indication.
 */
type ConverterLimit = {
    threshold: (componentInstance: DiagramComponentInstance, portIndex: number) => number | null;
    series: (port: PortLines) => number[] | null;
    outside: (sample: number, threshold: number) => boolean;
    sustainFor: number;
};

/**
 * The declared limit in base units for one DC port, or null. A null, a non-number and a
 * non-positive value all read as absent at every endpoint: zero is neither a floor a
 * converter can fall below nor a ceiling it can rise above, and treating it as one would arm
 * a converter that declared nothing. Mirrors `converter_voltage_minimum` in dcide-sim, which
 * applies the same unit scaling and the same `> 0` test.
 */
const getConverterPortLimit = (
    componentInstance: DiagramComponentInstance,
    portIndex: number,
    measurement: 'voltage' | 'current',
    endpoint: 'min' | 'max',
): number | null => {
    const port = componentInstance.specifications?.electrical?.ports?.[portIndex];
    const dc = (port && 'DC' in port ? port.DC : undefined) as
        | Partial<Record<'voltage' | 'current', { min?: number | null; max?: number | null; unit?: string }>>
        | undefined;

    const declaration = dc?.[measurement];
    const declared = declaration?.[endpoint];

    if (!isNumber(declared) || declared <= 0) return null;

    return measurement === 'voltage'
        ? voltageConverter.toBase({ value: declared, from: (declaration?.unit ?? 'V') as TVoltageUnit }).value
        : currentConverter.toBase({ value: declared, from: (declaration?.unit ?? 'A') as TCurrentUnit }).value;
};

/**
 * `sustainFor` is what separates the two voltage limits from the current one. A converter's
 * voltage comparators act within microseconds, so a single sample outside is the indication.
 * Its over-current protection does not: a converter rides out the capacitor discharge that
 * follows fault inception and trips on current that stays up, so a peak alone must not red
 * it. One millisecond is the engine's own `CONVERTER_UNDERVOLTAGE_WINDOW`, the only dwell
 * this product has agreed on, reused here rather than inventing a second number.
 */
const SUSTAINED_OVERCURRENT_SECONDS = 0.001;

const CONVERTER_LIMITS: Record<ConverterLimitKind, ConverterLimit> = {
    undervoltage: {
        threshold: (componentInstance, portIndex) =>
            getConverterPortLimit(componentInstance, portIndex, 'voltage', 'min'),
        series: getDcTerminalVoltage,
        outside: (sample, threshold) => sample < threshold,
        sustainFor: 0,
    },
    overvoltage: {
        threshold: (componentInstance, portIndex) =>
            getConverterPortLimit(componentInstance, portIndex, 'voltage', 'max'),
        series: getDcTerminalVoltage,
        outside: (sample, threshold) => sample > threshold,
        sustainFor: 0,
    },
    overcurrent: {
        threshold: (componentInstance, portIndex) =>
            getConverterPortLimit(componentInstance, portIndex, 'current', 'max'),
        series: getDcPortCurrent,
        outside: (sample, threshold) => sample > threshold,
        sustainFor: SUSTAINED_OVERCURRENT_SECONDS,
    },
};

/**
 * The first sample of a run that has been outside the limit for `sustainFor` seconds without
 * interruption, or null. A sample back inside resets the run rather than accumulating, so a
 * ring that crosses back and forth never adds up to a sustained excursion. With `sustainFor`
 * at zero the first sample outside qualifies on its own.
 *
 * Samples at or before fault inception are skipped: before the fault the terminal sits at its
 * operating point, which at one corner is the threshold itself, so a comparator running there
 * would report every converter as having left its limits in a solve where nothing had
 * happened yet. Mirrors `_sustained_undervoltage` in dcide-sim.
 */
const findSustainedExcursion = (
    series: number[],
    time: number[],
    threshold: number,
    faultTime: number,
    limit: ConverterLimit,
): number | null => {
    const samples = Math.min(series.length, time.length);

    let runStart: number | null = null;

    for (let index = 0; index < samples; index++) {
        if (!(time[index] > faultTime)) continue;
        if (!isNumber(series[index])) continue;

        if (!limit.outside(series[index], threshold)) {
            runStart = null;
            continue;
        }

        if (runStart === null) runStart = time[index];
        if (time[index] - runStart >= limit.sustainFor) return index;
    }

    return null;
};

/**
 * The earliest sample, across the converter's DC ports, at which one declared limit was left.
 * Equality is not a crossing at any endpoint.
 *
 * `tripped` is derived from the timestamp the viewer is on, not stored: the indication is
 * latched forward from the crossing and does not clear when the measurement recovers, but it
 * does clear when the viewer scrubs back before the crossing.
 *
 * Voltage is compared on the signed L+ − L− terminal voltage. A negative ring larger in
 * magnitude than `max` is therefore not an over-voltage — but it is already below `min`, so
 * the converter still reds, through the other limit.
 */
const findConverterLimitCrossing = (
    simulation: FaultSimulation,
    componentInstance: DiagramComponentInstance,
    timestampIndex: number,
    kind: ConverterLimitKind,
): ConverterLimitState | null => {
    if (componentInstance.componentType !== 'converter') return null;

    const time = simulation.result?.time;
    const ports = simulation.result?.componentInstances?.[componentInstance.id]?.ports;

    if (!Array.isArray(time) || time.length === 0 || !ports) return null;

    const faultTime = isNumber(simulation.faultTime) ? simulation.faultTime : 0;
    const limit = CONVERTER_LIMITS[kind];

    let earliest: ConverterLimitState | null = null;

    for (const [key, port] of Object.entries(ports)) {
        const portIndex = Number(key);

        if (!Number.isInteger(portIndex)) continue;
        if (componentInstance.configuration?.ports?.[portIndex]?.voltageType === 'AC') continue;

        const threshold = limit.threshold(componentInstance, portIndex);
        if (threshold === null) continue;

        const series = limit.series(port as PortLines);
        if (!series) continue;

        const crossingIndex = findSustainedExcursion(series, time, threshold, faultTime, limit);
        if (crossingIndex === null) continue;

        if (!earliest || crossingIndex < earliest.crossingIndex) {
            earliest = {
                kind,
                portIndex,
                threshold,
                crossingIndex,
                crossingTime: time[crossingIndex],
                tripped: false,
            };
        }
    }

    if (!earliest) return null;

    return { ...earliest, tripped: timestampIndex >= earliest.crossingIndex };
};

const MIN_DISPLAY_SPAN_SECONDS = 0.2;
const STEADY_TAIL_TOLERANCE = 0.05;

export const MINIMUM_DISPLAYED_FAULT_CURRENT = 1;

const hasSteadyTail = (values: number[]): boolean => {
    if (values.length < 2) return true;

    const amplitude = Math.max(...values) - Math.min(...values);
    if (amplitude === 0) return true;

    const lastStep = Math.abs(values[values.length - 1] - values[values.length - 2]);
    return lastStep <= STEADY_TAIL_TOLERANCE * amplitude;
};

const FaultSimulationHelpers = {
    MIN_DISPLAY_SPAN_SECONDS,

    padSeriesForDisplay: (time: number[], series: number[][]): { time: number[]; series: number[][] } => {
        if (time.length === 0) return { time, series };

        const span = time[time.length - 1] - time[0];
        if (time.length >= 2 && span >= MIN_DISPLAY_SPAN_SECONDS) return { time, series };

        if (!series.every(hasSteadyTail)) return { time, series };

        const boundary = time[0] + MIN_DISPLAY_SPAN_SECONDS;
        return {
            time: [...time, boundary],
            series: series.map((values) => [...values, values[values.length - 1]]),
        };
    },

    /**
     * Axis `[min, max]` for uPlot from series data. Equal or non-finite bounds
     * make `numAxisSplits` loop forever (incr of 0) and throw
     * `RangeError: Invalid array length`, so flat series are expanded.
     * `pad` is fractional headroom on a real span (default 0.1); use 0 for zoom.
     */
    getAxisRange: (arrays: number[][], pad = 0.1): [number, number] | null => {
        if (!arrays.length) return null;

        const allValues = arrays.flat().filter((value) => Number.isFinite(value));
        if (!allValues.length) return null;

        const min = Math.min(...allValues);
        const max = Math.max(...allValues);

        if (min === max) {
            const expand = min === 0 ? 1 : Math.abs(min) * 0.1;
            return [min - expand, max + expand];
        }

        const padding = (max - min) * pad;
        return [min - padding, max + padding];
    },

    swr: {
        faultSimulation: (diagramId: string) => `/diagram/${diagramId}/faultSimulations`,
    },

    generateName: {
        faultSimulation: (location?: number) =>
            `Fault Simulation${isNumber(location) ? ` (${location * 100}%) ` : ' '}${DateService.format(new Date())}`,
    },

    formatFaultTerminalsLabel: (lines: FaultLine[] | null | undefined) => {
        if (!lines || lines.length === 0) return null;
        const match = FAULT_TERMINAL_PAIR_LABELS.find(
            (entry) => entry.lines.length === lines.length && entry.lines.every((line) => lines.includes(line)),
        );
        return match?.label ?? lines.join(' ↔ ');
    },

    isLineToEarthFault: (lines: FaultLine[] | null | undefined): boolean => !!lines?.includes('PE'),

    getComponentTouchVoltageSeries: (simulation: FaultSimulation, componentInstanceId: string): number[] | null => {
        const ports = simulation.result?.componentInstances?.[componentInstanceId]?.ports;

        // A component can carry several PE terminals; the one that settles worst in magnitude is
        // the touch voltage its label, the summary and the graph all report.
        let worst: number[] | null = null;

        for (const port of Object.values(ports ?? {})) {
            const series = (port as { PE?: { voltage?: number[] } })?.PE?.voltage;
            if (!Array.isArray(series) || series.length === 0) continue;

            const last = series[series.length - 1];
            if (!isNumber(last)) continue;
            if (worst === null || Math.abs(last) > Math.abs(worst[worst.length - 1])) worst = series;
        }

        return worst;
    },

    getComponentTouchVoltageAt: (
        simulation: FaultSimulation,
        componentInstanceId: string,
        timestampIndex: number,
    ): number | null => {
        const series = FaultSimulationHelpers.getComponentTouchVoltageSeries(simulation, componentInstanceId);
        if (!series?.length) return null;

        const indexWithinSeries = Math.max(0, Math.min(timestampIndex, series.length - 1));
        const value = series[indexWithinSeries];
        return isNumber(value) ? value : null;
    },

    calculateComponentTouchVoltage: (simulation: FaultSimulation, componentInstanceId: string): number | null => {
        // The simulation document arrives before its result file; caching at that point would pin
        // null for the rest of the session.
        if (!simulation.result?.componentInstances) return null;

        const cached = FaultSimulationCacheService.getComponentTouchVoltage(simulation.id, componentInstanceId);
        if (cached !== undefined) return cached;

        const series = FaultSimulationHelpers.getComponentTouchVoltageSeries(simulation, componentInstanceId);
        const settled = series ? series[series.length - 1] : null;

        FaultSimulationCacheService.setComponentTouchVoltage(simulation.id, componentInstanceId, settled);
        return settled;
    },

    calculateMaxTouchVoltage: (simulation: FaultSimulation): { value: number; componentInstanceId: string } | null => {
        let worst: { value: number; componentInstanceId: string } | null = null;

        for (const componentInstanceId of Object.keys(simulation.result?.componentInstances ?? {})) {
            const value = FaultSimulationHelpers.calculateComponentTouchVoltage(simulation, componentInstanceId);
            if (!isNumber(value)) continue;
            if (worst === null || Math.abs(value) > Math.abs(worst.value)) worst = { value, componentInstanceId };
        }

        return worst;
    },

    /** A converter whose terminal voltage fell under its declared `DC.voltage.min`. */
    getConverterUndervoltage: (
        simulation: FaultSimulation,
        componentInstance: DiagramComponentInstance,
        timestampIndex: number,
    ): ConverterLimitState | null =>
        findConverterLimitCrossing(simulation, componentInstance, timestampIndex, 'undervoltage'),

    /**
     * A converter whose terminal voltage rose over its declared `DC.voltage.max`.
     *
     * Purely app-side. `PROTECTION_OUTCOME_REASONS` in dcide-sim is a closed set with no
     * over-voltage member, so unlike the under-voltage case there is never an engine verdict
     * to defer to.
     */
    getConverterOvervoltage: (
        simulation: FaultSimulation,
        componentInstance: DiagramComponentInstance,
        timestampIndex: number,
    ): ConverterLimitState | null =>
        findConverterLimitCrossing(simulation, componentInstance, timestampIndex, 'overvoltage'),

    /**
     * A converter whose pole current stayed over its declared `DC.current.max` for long
     * enough to be more than the discharge transient. Also app-side only: the engine reads
     * `DC.current.max` as a source rating, never as a converter trip threshold, and its
     * converter entry carries one reason, `undervoltage`.
     */
    getConverterOvercurrent: (
        simulation: FaultSimulation,
        componentInstance: DiagramComponentInstance,
        timestampIndex: number,
    ): ConverterLimitState | null =>
        findConverterLimitCrossing(simulation, componentInstance, timestampIndex, 'overcurrent'),

    isMixedVoltageZone: (diagram: Diagram, zone: Zone): boolean => {
        let hasAc = false;
        let hasDc = false;

        for (const connection of zone.connections) {
            const fromComponentInstance = diagram.componentInstances[connection.from.componentInstanceId];
            const toComponentInstance = diagram.componentInstances[connection.to.componentInstanceId];

            if (!fromComponentInstance || !toComponentInstance) continue;

            const voltageType = ConnectionHelpers.getVoltageType(
                connection,
                fromComponentInstance,
                toComponentInstance,
            );

            if (voltageType === 'AC') hasAc = true;
            if (voltageType === 'DC') hasDc = true;
        }

        return hasAc && hasDc;
    },

    componentIsProtectedDevice: (componentInstance: DiagramComponentInstance) => {
        const isLoad = ['load', 'charger', 'light', 'hvac', 'motor'].includes(componentInstance.componentType);
        return (
            isLoad || componentInstance.componentType === 'battery' || componentInstance.componentType === 'converter'
        );
    },

    getConnectionToProtectedDevice: (diagram: Diagram, componentInstance: DiagramComponentInstance) => {
        const connections = Object.values(diagram.connections);

        return connections.find((connection) => {
            const isFromComponent = connection.from.componentInstanceId === componentInstance.id;
            const isToComponent = connection.to.componentInstanceId === componentInstance.id;

            if (!isFromComponent && !isToComponent) return false;

            const connectedComponentId = isFromComponent
                ? connection.to.componentInstanceId
                : connection.from.componentInstanceId;

            const connectedComponent = diagram.componentInstances[connectedComponentId];

            if (!connectedComponent) return false;

            return FaultSimulationHelpers.componentIsProtectedDevice(connectedComponent);
        });
    },

    getCurrentSeries: (lines: Simulation['result']['connections']['number']['lines']): number[] =>
        getFaultCurrentSeries(lines),

    getFlowDirection: (current: number | null | undefined): 'generate' | 'load' | null => {
        if (!FaultSimulationHelpers.isDisplayedCurrent(current)) return null;
        return current! > 0 ? 'generate' : 'load';
    },

    isDisplayedCurrent: (current: number | null | undefined): boolean =>
        isNumber(current) && Math.abs(current) >= MINIMUM_DISPLAYED_FAULT_CURRENT,

    getVoltageSeries: (lines: Simulation['result']['connections']['number']['lines']): number[] => {
        const dcLines = lines as unknown as Record<
            string,
            | {
                  current?: number[];
                  voltage?: number[];
              }
            | undefined
        >;

        const lPlus = dcLines['L+']?.voltage;
        const lMinus = dcLines['L-']?.voltage;
        const m = dcLines['M']?.voltage;

        if (Array.isArray(lPlus) && Array.isArray(lMinus) && lPlus.length && lMinus.length) {
            return subtractVoltageRails(lPlus, lMinus);
        }

        if (Array.isArray(lPlus) && Array.isArray(m) && lPlus.length && m.length) {
            return subtractVoltageRails(lPlus, m);
        }

        if (Array.isArray(m) && Array.isArray(lMinus) && m.length && lMinus.length) {
            return subtractVoltageRails(m, lMinus);
        }

        return [];
    },

    buildSeriesBand: (
        nom: number[],
        min: number[] | undefined,
        max: number[] | undefined,
    ): { min: number[]; max: number[] } | undefined => {
        if (!min || !max || min.length !== nom.length || max.length !== nom.length || nom.length === 0) {
            return undefined;
        }
        return {
            min: min.map((value, index) => Math.min(value, max[index])),
            max: min.map((value, index) => Math.max(value, max[index])),
        };
    },

    // The engine solves each min/max scenario on its own adaptive time grid, so scenario series
    // rarely length-match the nominal series. Linear interpolation onto the nominal grid; values
    // are held flat outside the scenario's solved range (a scenario that settles early has reached
    // steady state, so holding the last sample is the physical continuation).
    resampleToTimeGrid: (sourceTime: number[], values: number[], targetTime: number[]): number[] => {
        if (sourceTime.length === 0 || sourceTime.length !== values.length) return [];

        let upper = 0;
        return targetTime.map((t) => {
            while (upper < sourceTime.length && sourceTime[upper] < t) upper += 1;
            if (upper === 0) return values[0];
            if (upper >= sourceTime.length) return values[values.length - 1];

            const span = sourceTime[upper] - sourceTime[upper - 1];
            if (span <= 0) return values[upper];
            const ratio = (t - sourceTime[upper - 1]) / span;
            return values[upper - 1] + (values[upper] - values[upper - 1]) * ratio;
        });
    },

    padSeriesHoldLast: (series: number[], targetLength: number): number[] => {
        if (series.length === 0 || series.length >= targetLength) return series;
        return [...series, ...Array(targetLength - series.length).fill(series[series.length - 1])];
    },

    buildScenarioBand: (
        nom: number[],
        nomTime: number[],
        min: { series: number[]; time?: number[] },
        max: { series: number[]; time?: number[] },
    ): { min: number[]; max: number[] } | undefined => {
        // A scenario series can stop before its own time grid ends (the solver settled), so first
        // hold-last pad it to that grid, then interpolate onto the nominal grid.
        const targetTime = nomTime.slice(0, nom.length);
        const align = ({ series, time }: { series: number[]; time?: number[] }) => {
            if (!time?.length) return series;
            const padded = FaultSimulationHelpers.padSeriesHoldLast(series, time.length);
            return FaultSimulationHelpers.resampleToTimeGrid(time, padded, targetTime);
        };

        return FaultSimulationHelpers.buildSeriesBand(nom, align(min), align(max));
    },

    getArcVoltageSeries: (lines: Simulation['result']['connections']['number']['lines']): number[] => {
        const dcLines = lines as unknown as Record<string, { v_arc?: number[] } | undefined>;
        return dcLines['L+']?.v_arc ?? dcLines['L-']?.v_arc ?? [];
    },

    getTouchVoltage: (
        simulation: FaultSimulation,
        connection: DiagramConnection,
    ): { from?: number; to?: number } | null => getFaultTouchVoltage(simulation.result, connection),

    J_PER_M2_PER_CAL_PER_CM2: JPM2_PER_CALPCM2,

    jPerM2ToCalPerCm2: (jPerM2: number): number => convertIncidentEnergy(jPerM2, 'J/m²', 'cal/cm²'),

    pickEArcDisplayUnit,

    NOISE_FLOOR: 1,

    isNoiseCurrent: (value: number): boolean => Math.abs(value) < FaultSimulationHelpers.NOISE_FLOOR,

    formatFaultCurrent: (value: number): string => {
        const displayValue = FaultSimulationHelpers.isNoiseCurrent(value) ? 0 : value;

        return FormatHelpers.formatCurrent({ value: displayValue });
    },

    formatFaultMetric: (
        metric: { value: number; unit: string } | undefined,
        kind: 'peakCurrent' | 'diDt_10_90' | 'i2t',
    ): string => {
        if (!metric || !isNumber(metric.value) || Number.isNaN(metric.value)) return '—';

        if (kind === 'peakCurrent') {
            return FaultSimulationHelpers.formatFaultCurrent(metric.value);
        }
        if (kind === 'i2t') {
            return FormatHelpers.formatMeasurement({ value: metric.value, unit: 'A²•s' }, i2tConverter);
        }
        return FormatHelpers.formatMeasurement({ value: metric.value, unit: 'A/s' }, currentSlewRateConverter);
    },

    getConnectionColor: (
        simulation: FaultSimulation,
        connectionId: string,
        type: 'lines' | 'from' | 'to' = 'lines',
    ) => {
        const isFaultConnection = simulation.faultConnectionId === connectionId;

        if (isFaultConnection) return type === 'to' ? 'orange.6' : 'teal.6';

        const index = Object.keys(simulation.result.connections)
            .filter((id) => simulation.faultConnectionId !== id)
            .findIndex((id) => id === connectionId);

        const colors = ['blue.3', 'grape.3', 'violet.3', 'pink.3', 'blue.7', 'grape.7', 'violet.7', 'pink.7'];

        return colors[index % colors.length];
    },

    getComponentInstanceColor: (simulation: FaultSimulation, componentInstanceId: string) => {
        const index = Object.keys(simulation.result?.componentInstances ?? {}).indexOf(componentInstanceId);

        return COMPONENT_INSTANCE_COLORS[Math.max(index, 0) % COMPONENT_INSTANCE_COLORS.length];
    },

    isStale: (simulation: FaultSimulation, faultZone: Zone | null | undefined): boolean => {
        if (!faultZone) return false;
        return !!simulation.hash && simulation.hash !== faultZone.hash;
    },

    getBatchSimulationsStatuses: (batchSimulations: FaultSimulation[]) => {
        return Object.values(JobStatus)
            .map((status) => ({
                status,
                simulations: batchSimulations.filter((s) => s.status === status),
            }))
            .filter(({ simulations }) => !!simulations.length);
    },

    // A simulation can be marked done before its (heavy) result payload has been fetched into the
    // front-end. In that window the charts have nothing to draw yet, so we treat it as still loading.
    isAwaitingResults: (simulation: FaultSimulation) =>
        simulation.status === JobStatus.DONE && !simulation.result?.time && simulation.nbTimestamps !== 0,

    filterByBatchId: (simulations: FaultSimulation[], batchId?: string) => {
        if (!batchId) return [];

        return simulations.filter((sim) => sim.batchId === batchId).sort((a, b) => a.faultLocation - b.faultLocation);
    },

    getBatchKey: (batch: FaultSimulation[]): string => batch[0].batchId ?? batch[0].id,

    groupByBatch: (simulations: FaultSimulation[]): FaultSimulation[][] => {
        const batchIds = unique(simulations.map((sim) => sim.batchId || sim.id));

        return batchIds
            .map((batchId) => {
                const batchSimulations = FaultSimulationHelpers.filterByBatchId(simulations, batchId);
                if (batchSimulations.length) return batchSimulations;

                const single = simulations.find((sim) => sim.id === batchId);
                return single ? [single] : [];
            })
            .filter((batch) => batch.length)
            .sort(
                (a, b) =>
                    Math.max(...b.map((sim) => new Date(sim.createdAt ?? 0).getTime())) -
                    Math.max(...a.map((sim) => new Date(sim.createdAt ?? 0).getTime())),
            );
    },

    isBatchActive: (simulations: FaultSimulation[], activeSimulationId?: string, batchId?: string) => {
        if (!activeSimulationId || !batchId) return false;

        return simulations.some((sim) => sim.id === activeSimulationId && (sim.batchId || sim.id) === batchId);
    },

    getMostRecentFaultSimulation: (
        simulations: FaultSimulation[],
        faultConnectionId: string,
        faultLocation: number,
    ): FaultSimulation | undefined => {
        return simulations
            .filter((simulation) => {
                return (
                    simulation.faultConnectionId === faultConnectionId &&
                    simulation.faultLocation === faultLocation &&
                    simulation.status !== JobStatus.ERROR
                );
            })
            .sort((a, b) => {
                return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
            })[0];
    },

    getZoneMinVoltage: (
        diagram: Diagram,
        zone: Zone,
        allSimulations: FaultSimulation[],
    ): {
        value: number;
        source: 'custom' | 'zone';
        simulationId?: string;
        componentInstances?: string[];
    } => {
        const zoneSimulations = allSimulations.filter((sim) => {
            const simZone = ZoneHelpers.getZoneForConnection([zone], sim.faultConnectionId);
            return simZone?.id === zone.id;
        });

        const simulationsWithCustomVoltage = zoneSimulations
            .filter((sim) => sim.minVoltage?.value)
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

        if (simulationsWithCustomVoltage.length > 0) {
            const latestSim = simulationsWithCustomVoltage[0];
            return {
                value: latestSim.minVoltage!.value,
                source: 'custom',
                simulationId: latestSim.id,
            };
        }

        const { min, minComponentInstances } = getMinAndMaxVoltageForZone(diagram, zone);
        return {
            value: min,
            source: 'zone',
            componentInstances: minComponentInstances,
        };
    },

    calculateCriticalClearingTime: (
        diagram: Diagram,
        zones: Zone[],
        protectionZones: Zone[],
        simulation: FaultSimulation,
        allSimulations: FaultSimulation[],
    ): CriticalClearingTimeResult | null => {
        const cached = FaultSimulationCacheService.getCriticalClearingTime(simulation.id);
        if (cached !== undefined) {
            return cached;
        }

        const faultConnectionId = simulation.faultConnectionId;

        const zone = ZoneHelpers.getZoneForConnection(zones, faultConnectionId);

        if (!zone) {
            FaultSimulationCacheService.setCriticalClearingTime(simulation.id, null);
            return null;
        }

        let minVoltageData: {
            minVoltage: number;
            source: 'custom' | 'zone' | 'inherited';
            simulationId?: string;
            componentInstances?: string[];
        };

        if (simulation.minVoltage?.value) {
            minVoltageData = {
                minVoltage: simulation.minVoltage.value,
                source: 'custom',
            };
        } else {
            const info = FaultSimulationHelpers.getZoneMinVoltage(diagram, zone, allSimulations);
            minVoltageData = {
                minVoltage: info.value,
                source: info.source === 'custom' ? 'inherited' : 'zone',
                simulationId: info.simulationId,
                componentInstances: info.componentInstances,
            };
        }

        const faultConnection = diagram.connections[faultConnectionId];
        if (!faultConnection) {
            FaultSimulationCacheService.setCriticalClearingTime(simulation.id, null);
            return null;
        }

        let criticalClearingTime: number | null = null;

        const voltageData: Array<{
            componentInstanceId: string;
            port: string;
            voltageSeries: number[];
            crossingTime?: number;
        }> = [];

        const faultEndpointComponentIds = [
            faultConnection.from.componentInstanceId,
            faultConnection.to.componentInstanceId,
        ];

        const faultProtectionZone = ZoneHelpers.getZoneForConnection(protectionZones, faultConnectionId);
        const loadsInFaultProtectionZone = faultProtectionZone
            ? faultProtectionZone.componentInstances.filter((ci) => ci.componentType === 'load').map((ci) => ci.id)
            : [];

        zone.componentInstances
            .filter((componentInstance) => FaultSimulationHelpers.componentIsProtectedDevice(componentInstance))
            .filter((componentInstance) => !faultEndpointComponentIds.includes(componentInstance.id))
            .filter((componentInstance) => {
                if (componentInstance.componentType !== 'load') return true;
                return !loadsInFaultProtectionZone.includes(componentInstance.id);
            })
            .forEach((componentInstance) => {
                const componentInstanceResult = simulation.result.componentInstances?.[componentInstance.id];
                if (!componentInstanceResult) return;

                Object.entries(componentInstanceResult.ports).forEach(([port, portResult]) => {
                    if (!portResult) return;

                    voltageData.push({
                        componentInstanceId: componentInstance.id,
                        port,
                        voltageSeries: FaultSimulationHelpers.getVoltageSeries(portResult),
                    });
                });
            });

        if (voltageData.length === 0) {
            const result = {
                zone,
                ...minVoltageData,
                criticalClearingTime,
            };
            FaultSimulationCacheService.setCriticalClearingTime(simulation.id, result);
            return result;
        }

        const time = simulation.result.time || [];

        voltageData.forEach((data) => {
            const currentCrossesTimeIndex = data.voltageSeries.findIndex(
                (voltage) => voltage < minVoltageData.minVoltage,
            );

            if (currentCrossesTimeIndex > 0) {
                const interpolatedTime = NumberHelpers.interpolate({
                    x1: time[currentCrossesTimeIndex - 1],
                    x2: time[currentCrossesTimeIndex],
                    y1: data.voltageSeries[currentCrossesTimeIndex - 1],
                    y2: data.voltageSeries[currentCrossesTimeIndex],
                    targetY: minVoltageData.minVoltage,
                });

                data.crossingTime = interpolatedTime;
            }
        });

        const validCrossingTimes = voltageData
            .filter((data) => isNumber(data.crossingTime))
            .map((data) => data.crossingTime as number);

        if (validCrossingTimes.length > 0) {
            criticalClearingTime = Math.min(...validCrossingTimes);
        }

        const firstCrossingComponents = unique(
            voltageData
                .filter((data) => data.crossingTime === criticalClearingTime)
                .map(({ componentInstanceId, port }) => ({
                    componentInstanceId,
                    port,
                })),
            (item) => item.componentInstanceId,
        );

        const result = {
            zone,
            ...minVoltageData,
            criticalClearingTime,
            firstCrossingComponents,
        };

        FaultSimulationCacheService.setCriticalClearingTime(simulation.id, result);
        return result;
    },

    calculateFusesI2t: (diagram: Diagram, zones: Zone[], simulation: FaultSimulation): FuseI2TResult[] => {
        const time = simulation.result.time;
        const faultTime = simulation.faultTime;

        const cached = FaultSimulationCacheService.getFusesI2t(simulation.id);
        if (cached !== undefined) {
            return cached;
        }

        if (!time || !simulation?.result?.componentInstances || !simulation?.faultConnectionId) return [];

        const faultTimeIndex = time.findIndex((t) => t >= faultTime);
        if (faultTimeIndex === -1) return [];

        const faultConnection = diagram.connections[simulation.faultConnectionId];
        if (!faultConnection) return [];

        const zone = ZoneHelpers.getZoneForConnection(zones, simulation.faultConnectionId);

        if (!zone) return [];

        const i2tResults: FuseI2TResult[] = [];

        zone.componentInstances.forEach((componentInstance) => {
            if (componentInstance.componentType !== 'fuse') return;

            const fuseId = componentInstance.id;
            const fuseResult = simulation.result.componentInstances[fuseId];

            if (!fuseResult) return;

            const isFaultFuse =
                faultConnection.from.componentInstanceId === fuseId ||
                faultConnection.to.componentInstanceId === fuseId;

            const result = FaultSimulationHelpers.calculateFuseI2t({
                simulation,
                fuse: componentInstance,
            });

            if (result) {
                i2tResults.push({ ...result, isFaultFuse });
            }
        });

        const result = i2tResults.sort((a, b) => (a.isFaultFuse === b.isFaultFuse ? 0 : a.isFaultFuse ? -1 : 1));
        FaultSimulationCacheService.setFusesI2t(simulation.id, result);
        return result;
    },

    calculateFuseI2t: ({
        simulation,
        fuse,
        endTimeIndex,
        scenario = 'nom',
    }: {
        simulation: FaultSimulation;
        fuse: DiagramComponentInstance;
        endTimeIndex?: number;
        scenario?: 'nom' | 'min' | 'max';
    }): FuseI2TResult | null => {
        const fuseId = fuse.id;
        const cacheKey = scenario === 'nom' ? fuseId : `${fuseId}::${scenario}`;

        const cached = FaultSimulationCacheService.getFuseI2t(simulation.id, cacheKey, endTimeIndex);
        if (cached !== undefined) {
            return cached;
        }

        const scenarioResult = scenario === 'nom' ? simulation.result : simulation.result.scenarios?.[scenario];
        const fuseResult = scenarioResult?.componentInstances?.[fuseId];
        const time = scenarioResult?.time ?? simulation.result.time;
        const faultTime = simulation.faultTime;

        if (!time || !fuseResult) {
            FaultSimulationCacheService.setFuseI2t(simulation.id, cacheKey, endTimeIndex, null);
            return null;
        }

        const faultTimeIndex = time.findIndex((t) => t >= faultTime);

        if (faultTimeIndex === -1) {
            FaultSimulationCacheService.setFuseI2t(simulation.id, cacheKey, endTimeIndex, null);
            return null;
        }

        const ports = fuseResult.ports;

        if (!ports) return null;

        const fuseElectrical = fuse.specifications?.electrical as Fuse['electrical'];
        const meltingI2tValue = fuseElectrical?.meltingI2t?.value;

        for (const [_, port] of Object.entries(ports)) {
            const currentSeries = FaultSimulationHelpers.getCurrentSeries(port);
            if (!currentSeries || currentSeries.length === 0) continue;

            let i2t = 0;
            let meltingTime: number | undefined;

            const maxIndex = endTimeIndex ?? currentSeries.length - 1;

            for (let i = faultTimeIndex; i < maxIndex; i++) {
                const dt = time[i + 1] - time[i];
                const avgCurrent = (Math.abs(currentSeries[i]) + Math.abs(currentSeries[i + 1])) / 2;
                i2t += avgCurrent * avgCurrent * dt;

                if (meltingI2tValue && i2t >= meltingI2tValue && !meltingTime) {
                    meltingTime = time[i + 1];
                }
            }

            const result = {
                fuseId,
                fuseLabel: LabelService.getComponentLabel(fuse),
                i2t,
                meltingI2t: meltingI2tValue ?? undefined,
                meltingTime,
            };

            FaultSimulationCacheService.setFuseI2t(simulation.id, cacheKey, endTimeIndex, result);
            return result;
        }

        FaultSimulationCacheService.setFuseI2t(simulation.id, cacheKey, endTimeIndex, null);
        return null;
    },

    getProtectionState: ({
        simulation,
        componentInstance,
        timestamp,
        zones,
    }: {
        simulation: FaultSimulation;
        componentInstance: DiagramComponentInstance;
        timestamp?: number;
        zones?: Zone[];
    }): ProtectionDeviceState | null => {
        const entry = simulation.result.protection?.[componentInstance.id];

        if (!entry && !isProtectionDevice(componentInstance.componentType)) return null;

        const metric: ProtectionMetric = PROTECTION_METRICS[componentInstance.componentType] ?? 'current';

        if (!entry && zones) {
            const zone = ZoneHelpers.getZoneForConnection(zones, simulation.faultConnectionId);

            if (!zone?.componentInstances.some(({ id }) => id === componentInstance.id)) return null;
        }

        if (entry) {
            const measurement = { current: entry.peakCurrent, i2t: entry.i2t, voltage: entry.minimumVoltage }[metric];
            const measured = {
                metric,
                value: measurement?.value,
                valueUnit: measurement?.unit,
                threshold: entry.threshold?.value,
                thresholdUnit: entry.threshold?.unit,
                reason: entry.reason,
                unitMismatch:
                    hasUnexpectedUnit(metric, measurement?.unit) || hasUnexpectedUnit(metric, entry.threshold?.unit),
            };

            if (!entry.modelled) {
                return { status: 'not-modelled', open: false, tripTime: null, ...measured };
            }

            if (entry.tripped === null || entry.tripped === undefined) {
                return { status: 'inconclusive', open: false, tripTime: null, ...measured };
            }

            if (!entry.tripped) {
                return { status: 'did-not-trip', open: false, tripTime: null, ...measured };
            }

            const tripTime = isNumber(entry.tripTime) ? entry.tripTime : null;
            const lastTimestamp = simulation.result.time?.at(-1);
            const isReachable = isNumber(tripTime) && (!isNumber(lastTimestamp) || tripTime <= lastTimestamp);

            if (!isReachable) {
                return { status: 'tripped-time-unknown', open: false, tripTime: null, ...measured };
            }

            return {
                status: 'tripped',
                open: isNumber(timestamp) && timestamp >= tripTime,
                tripTime,
                ...measured,
            };
        }

        if (simulation.enableProtectionTripping) {
            return { status: 'not-reported', open: false, tripTime: null, metric };
        }

        if (componentInstance.componentType !== 'fuse') {
            return { status: 'not-modelled', open: false, tripTime: null, metric };
        }

        const fuseResult = FaultSimulationHelpers.calculateFuseI2t({ simulation, fuse: componentInstance });

        if (!fuseResult || !isNumber(fuseResult.meltingI2t)) {
            return { status: 'not-modelled', open: false, tripTime: null, metric };
        }

        if (!isNumber(fuseResult.meltingTime)) {
            return {
                status: 'did-not-trip',
                open: false,
                tripTime: null,
                metric,
                value: fuseResult.i2t,
                threshold: fuseResult.meltingI2t,
            };
        }

        return {
            status: 'tripped',
            open: isNumber(timestamp) && timestamp >= fuseResult.meltingTime,
            tripTime: fuseResult.meltingTime,
            metric,
            value: fuseResult.i2t,
            threshold: fuseResult.meltingI2t,
        };
    },

    calculateComponentPeakCurrent: (
        simulation: FaultSimulation,
        componentInstanceId: string,
        port: string | number,
    ): number => {
        // Same load order as calculateComponentTouchVoltage: caching before the result file is
        // present would pin 0 for the session.
        if (!simulation.result?.componentInstances) return 0;

        const portKey = String(port);
        const cached = FaultSimulationCacheService.getComponentFaultCurrent(
            simulation.id,
            componentInstanceId,
            portKey,
        );
        if (cached !== undefined) {
            return cached;
        }

        const portResult = simulation.result?.componentInstances?.[componentInstanceId]?.ports[portKey];
        if (!portResult) {
            FaultSimulationCacheService.setComponentFaultCurrent(simulation.id, componentInstanceId, portKey, 0);
            return 0;
        }

        const currents = FaultSimulationHelpers.getCurrentSeries(portResult);
        const maxCurrent = currents.length > 0 ? Math.max(...currents.map((c) => Math.abs(c))) : 0;

        FaultSimulationCacheService.setComponentFaultCurrent(simulation.id, componentInstanceId, portKey, maxCurrent);
        return maxCurrent;
    },

    calculatePeakCurrents: (diagram: Diagram, zones: Zone[], simulation: FaultSimulation): PeakCurrentResult[] => {
        const cached = FaultSimulationCacheService.getPeakCurrents(simulation.id);
        if (cached !== undefined) {
            return cached;
        }

        if (!simulation?.result?.componentInstances || !simulation?.faultConnectionId) return [];

        const faultConnection = diagram.connections[simulation.faultConnectionId];
        if (!faultConnection) return [];

        const zone = ZoneHelpers.getZoneForConnection(zones, simulation.faultConnectionId);

        if (!zone) return [];

        const protectionDeviceResults: PeakCurrentResult[] = [];

        zone.componentInstances.forEach((componentInstance) => {
            if (!isProtectionDevice(componentInstance.componentType)) return;

            const componentId = componentInstance.id;
            const deviceResult = simulation.result.componentInstances[componentId];

            if (!deviceResult) return;

            const isFaultDevice =
                faultConnection.from.componentInstanceId === componentId ||
                faultConnection.to.componentInstanceId === componentId;

            let peakCurrent = 0;

            Object.entries(deviceResult.ports || {}).forEach(([_, port]) => {
                if (!port) return;

                const currentSeries = FaultSimulationHelpers.getCurrentSeries(port);

                if (!currentSeries || currentSeries.length === 0) return;

                const portPeakCurrent = Math.max(...currentSeries.map(Math.abs));
                peakCurrent = Math.max(peakCurrent, portPeakCurrent);
            });

            protectionDeviceResults.push({
                componentId,
                componentLabel: LabelService.getComponentLabel(componentInstance),
                componentType: componentInstance.componentType,
                peakCurrent,
                isFaultDevice,
            });
        });

        const result = protectionDeviceResults.sort((a, b) =>
            a.isFaultDevice === b.isFaultDevice ? 0 : a.isFaultDevice ? -1 : 1,
        );

        FaultSimulationCacheService.setPeakCurrents(simulation.id, result);
        return result;
    },
};

export { FaultSimulationHelpers };
